type BookingExtraValue = {
  flag_extra_limit?: boolean
  quantity?: number | string
}

function parseBookingDateParts(date: string): { day: number, month: number, year: number } | null {
  const parts = (date ?? '').split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null

  const [first, second, third] = parts
  if (first === undefined || second === undefined || third === undefined) return null

  // yyyy-mm-dd
  if (first > 31) {
    return { year: first, month: second, day: third }
  }

  // dd-mm-yyyy
  return { day: first, month: second, year: third }
}

function bookingDateTimeParts(booking: Booking): { day: number, month: number, year: number, hours: number, minutes: number } | null {
  const parsed = parseBookingDateParts(booking.date ?? '')
  if (!parsed) return null

  const time = formatTime(booking.hour ?? '')
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

  return { ...parsed, hours, minutes }
}

/** Horas extra de la reserva (extras + promo), misma lógica que BookingCard. */
export function getBookingExtraHours(booking: Booking): number {
  const extrasMap = (booking.extras ?? {}) as Record<string, BookingExtraValue>
  let total = Object.values(extrasMap).reduce((acc, extra) => {
    if (extra.flag_extra_limit) {
      return acc + (Number(extra.quantity) || 0)
    }
    return acc
  }, 0)

  const room = booking.room_type
  if (room?.promo?.type === 2) {
    total += Number(room.promo.extra_hours) || 0
  }

  return total
}

export function getBookingPackHours(booking: Booking): number {
  const raw = booking.pack_hours
  const pack = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(pack) ? pack : 0
}

/** `date` llega como `dd-mm-yyyy` (o `yyyy-mm-dd`) y `hour` como `HH:mm`. Devuelve timestamp para ordenar. */
export function bookingTimestamp(booking: Booking): number {
  const parts = bookingDateTimeParts(booking)
  if (!parts) return Number.NaN

  const timestamp = new Date(parts.year, parts.month - 1, parts.day, parts.hours, parts.minutes).getTime()
  return Number.isNaN(timestamp) ? Number.NaN : timestamp
}

/** Timestamp de fin de estancia: check-in + pack_hours + extras (misma duración que CheckDates / calculateCheckOut). */
export function bookingEndTimestamp(booking: Booking): number {
  const start = bookingTimestamp(booking)
  if (Number.isNaN(start)) return Number.NaN

  const totalHours = getBookingPackHours(booking) + getBookingExtraHours(booking)
  return start + totalHours * 60 * 60 * 1000
}

function cancelledFlag(value: unknown): boolean {
  if (value === true) return true
  if (typeof value === 'number' && value !== 0) return true
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }
  return false
}

/** Reserva cancelada (tolera valores no booleanos que pueda devolver la API). */
export function isBookingCancelled(booking: Pick<Booking, 'cancelled'>): boolean {
  return cancelledFlag(booking.cancelled)
}

/** Reserva aún por disfrutar (no cancelada y la estancia no ha terminado). */
export function isBookingUpcoming(booking: Booking, now = Date.now()): boolean {
  if (isBookingCancelled(booking)) return false
  const end = bookingEndTimestamp(booking)
  return !Number.isNaN(end) && end > now
}

/** Reserva ya disfrutada (no cancelada y la estancia ha terminado). */
export function isBookingCompleted(booking: Booking, now = Date.now()): boolean {
  if (isBookingCancelled(booking)) return false
  const end = bookingEndTimestamp(booking)
  return !Number.isNaN(end) && end <= now
}

/** Días transcurridos desde el check-in de la reserva (0 = hoy). */
export function daysSinceBooking(booking: Booking, now = Date.now()): number {
  const timestamp = bookingTimestamp(booking)
  if (Number.isNaN(timestamp)) return Number.POSITIVE_INFINITY
  const diffMs = now - timestamp
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

export function isBookingWithinRepeatWindow(
  booking: Booking,
  windowDays: number,
  now = Date.now()
): boolean {
  if (isBookingCancelled(booking)) return false
  if (!isBookingCompleted(booking, now)) return false
  const days = daysSinceBooking(booking, now)
  return days >= 0 && days <= windowDays
}
